// eslint-disable-next-line import/no-unresolved
import { PaymentAction } from '@adyen/adyen-web/dist/types/types';
import React, { ReactElement, useReducer, useContext, createContext } from 'react';

import {
  MergedRetryData,
  RetryProcessAuthorisationResponse,
} from '../../adapters/retryApiAdapter.types';
import { retryReducer } from '../../reducers/payment-retry/retry-data';
import { RetryActions, RetryState } from '../../reducers/payment-retry/types';

interface RetryDataContextProps {
  retryState: RetryState;
  retrySuccessWithData: (retryData: MergedRetryData) => void;
  initiateRetryPaymentStart: () => void;
  initiateRetryPaymentFailure: () => void;
  initiateRetryPaymentSuccess: (actionObject: PaymentAction, paymentId: string) => void;
  processPaymentAuthorisationStart: () => void;
  processPaymentAuthorisationFailure: () => void;
  processPaymentAuthorisationSuccess: (
    authorisationResult: RetryProcessAuthorisationResponse,
  ) => void;
}

export const RetryDataContext = createContext<RetryDataContextProps>({
  retryState: {
    retryData: undefined,
    initiatePaymentData: undefined,
    processAuthorisationData: undefined,
    customerId: undefined,
  },
  retrySuccessWithData: () => null,
  initiateRetryPaymentStart: () => null,
  initiateRetryPaymentFailure: () => null,
  initiateRetryPaymentSuccess: () => null,
  processPaymentAuthorisationStart: () => null,
  processPaymentAuthorisationFailure: () => null,
  processPaymentAuthorisationSuccess: () => null,
});

type Props = { customerId: string };

export const RetryDataProvider: React.FC<Props> = ({ children, customerId }): ReactElement => {
  const [state, dispatch] = useReducer(retryReducer, {
    retryData: undefined,
    initiatePaymentData: undefined,
    processAuthorisationData: undefined,
    customerId,
  });

  const retrySuccessWithData = (retryData: MergedRetryData) => {
    dispatch({ type: RetryActions.retry_fetch_success, payload: retryData });
  };

  const initiateRetryPaymentStart = () => {
    dispatch({ type: RetryActions.initiate_payment_start });
  };

  const initiateRetryPaymentFailure = () => {
    dispatch({ type: RetryActions.initiate_payment_failure });
  };

  const initiateRetryPaymentSuccess = (
    actionObject: PaymentAction,
    paymentId: string,
    authorizationId?: string,
  ) => {
    dispatch({
      type: RetryActions.initiate_payment_success,
      payload: { action: actionObject, paymentId, authorizationId },
    });
  };

  const processPaymentAuthorisationStart = () => {
    dispatch({ type: RetryActions.process_payment_authorisation_start });
  };

  const processPaymentAuthorisationFailure = () => {
    dispatch({ type: RetryActions.process_payment_authorisation_failure });
  };

  const processPaymentAuthorisationSuccess = (
    authorisationResult: RetryProcessAuthorisationResponse,
  ) => {
    dispatch({
      type: RetryActions.process_payment_authorisation_success,
      payload: authorisationResult,
    });
  };

  return (
    <RetryDataContext.Provider
      value={{
        retryState: state,
        retrySuccessWithData,
        initiateRetryPaymentFailure,
        initiateRetryPaymentStart,
        initiateRetryPaymentSuccess,
        processPaymentAuthorisationStart,
        processPaymentAuthorisationFailure,
        processPaymentAuthorisationSuccess,
      }}>
      {children}
    </RetryDataContext.Provider>
  );
};

export const useRetryState = () => {
  return useContext(RetryDataContext);
};

export default RetryDataProvider;
